import type { FC, ReactNode } from 'react';
import { BriefcaseBusiness, CalendarDays, CheckCircle2, PenLine, FileText, TrendingDown, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Loader } from '@/components/ui';
import { useDashboardStats } from '@/lib/api/hooks/dashboard';

interface StatsCardProps {
	title: string;
	value: number;
	description: ReactNode;
	icon: ReactNode;
	iconContainerClassName: string;
	iconClassName: string;
}

type DashboardStatKey = 'total' | 'published' | 'draft' | 'upcomingEvents' | 'openOpportunities';

interface StatConfig {
	key: DashboardStatKey;
	title: string;
	icon: LucideIcon;
	iconContainerClassName: string;
	iconClassName: string;
	getValue: (stats: NonNullable<ReturnType<typeof useDashboardStats>['data']>) => number;
	getChange: (stats: NonNullable<ReturnType<typeof useDashboardStats>['data']>) => number;
	getTrendLabel: (comparisonWindowDays: number) => string;
}

const StatCard = ({ title, value, description, icon, iconContainerClassName, iconClassName }: StatsCardProps) => {
	return (
		<div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
			<div className="flex   items-start gap-4">
				<div className={`flex h-10 w-10  items-center justify-center rounded-lg ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div className="min-w-0 flex flex-col">
					<p className="font-medium text-gray-600">{title}</p>

					<p className="mt-1 text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
					<div className="mt-2 text-sm flex flex-wrap gap-1 items-center text-gray-500">{description}</div>

				</div>
			</div>
		</div>
	);
};

const formatPercent = (value: number): string => `${Math.abs(value).toFixed(0)}%`;

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Pages',
		icon: FileText,
		iconContainerClassName: 'bg-[#F8E8EB90]',
		iconClassName: 'text-[#8F2F44]',
		getValue: (stats) => stats.content.total.totalCount,
		getChange: (stats) => stats.content.total.percentageChange,
		getTrendLabel: (comparisonWindowDays) => `vs last ${comparisonWindowDays} days`,
	},
	{
		key: 'published',
		title: 'Published Content',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-[#E8F6EE90]',
		iconClassName: 'text-[#2F8A57]',
		getValue: (stats) => stats.content.published.totalCount,
		getChange: (stats) => stats.content.published.percentageChange,
		getTrendLabel: (comparisonWindowDays) => `vs last ${comparisonWindowDays} days`,
	},
	{
		key: 'draft',
		title: 'Drafts',
		icon: PenLine,
		iconContainerClassName: 'bg-[#FDF3E690]',
		iconClassName: 'text-[#C58A12]',
		getValue: (stats) => stats.content.draft.totalCount,
		getChange: (stats) => stats.content.draft.percentageChange,
		getTrendLabel: (comparisonWindowDays) => `vs last ${comparisonWindowDays} days`,
	},
	{
		key: 'upcomingEvents',
		title: 'Upcoming Events',
		icon: CalendarDays,
		iconContainerClassName: 'bg-[#E8F6EE90]',
		iconClassName: 'text-[#2F8A57]',
		getValue: (stats) => stats.operational.upcomingEvents.totalCount,
		getChange: (stats) => stats.operational.upcomingEvents.percentageChange,
		getTrendLabel: (comparisonWindowDays) => `vs last ${comparisonWindowDays} days`,
	},
	{
		key: 'openOpportunities',
		title: 'Open Opportunities',
		icon: BriefcaseBusiness,
		iconContainerClassName: 'bg-[#F8E8EB90]',
		iconClassName: 'text-[#8F2F44]',
		getValue: (stats) => stats.operational.openOpportunities.totalCount,
		getChange: (stats) => stats.operational.openOpportunities.percentageChange,
		getTrendLabel: (comparisonWindowDays) => `vs last ${comparisonWindowDays} days`,
	},
];

const DashboardStats: FC = () => {
	const { data, isFetching, isError, refetch } = useDashboardStats();


	if (isError) {
		return (
			<div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
				<div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
					<p className="text-sm text-gray-600">Error loading dashboard statistics.</p>
					<button onClick={() => refetch()} className="rounded-lg bg-[#681628] px-4 py-2 text-sm font-medium text-white">
						Retry
					</button>
				</div>
			</div>
		);
	}

	if (isFetching || !data) {
		return (
			<section className="grid grid-cols-1 gap-4 xl:grid-cols-5">
				{Array.from({ length: 5 }).map((_, index) => (
					<div key={index} className="flex min-h-[132px] items-center justify-center rounded-xl border border-gray-200 bg-white shadow-sm">
						<Loader size="md" variant="wine" />
					</div>
				))}
			</section>
		);
	}

	const comparisonWindowDays = data.content.comparisonWindowDays || data.operational.comparisonWindowDays || 30;

	return (
		<section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
			{STATS_CONFIG.map((stat) => {
				const Icon = stat.icon;
				const value = stat.getValue(data);
				const change = stat.getChange(data);
				const isPositive = change > 0;
				const isNegative = change < 0;
				const TrendIcon = isNegative ? TrendingDown : TrendingUp;
				const trendColorClass = isPositive ? 'text-emerald-600' : isNegative ? 'text-red-500' : 'text-gray-500';

				return (
					<StatCard
						key={stat.key}
						title={stat.title}
						value={value}
						description={(
							<div className={`flex items-center flex-wrap gap-1 ${trendColorClass}`}>
								<TrendIcon className="h-4 w-3" />
								<span className="font-medium">{formatPercent(change)}</span>
								<span className="text-gray-500">{stat.getTrendLabel(comparisonWindowDays)}</span>
							</div>
						)}
						icon={<Icon className="h-4 w-4 md:h-6 md:w-6"  />}
						iconContainerClassName={stat.iconContainerClassName}
						iconClassName={stat.iconClassName}
					/>
				);
			})}
		</section>
	);
};

export default DashboardStats;
